import { type ComponentProps, type ReactNode, useId } from 'react';

import { cn } from '../lib/cn';
import { Title } from './title';

export interface SectionProps extends Omit<ComponentProps<'section'>, 'title'> {
  /** 구획 제목 — `Title level="card"`. 없으면 이름 없는 묶음이다. */
  title?: ReactNode;
  /** @default 'h2' */
  titleAs?: 'h2' | 'h3' | 'h4';
  /** 제목 크기. `label`은 레일·목록 열처럼 좁은 자리의 작은 구획이다. @default 'card' */
  titleLevel?: 'card' | 'label';
  /** 제목 줄 오른쪽 동작 하나 — 링크나 버튼. */
  action?: ReactNode;
  /** 앞 구획과 위 구분선으로 나눈다. 테두리 상자 대신 쓰는 구분이다. */
  divided?: boolean;
}

/**
 * 화면 안 구획의 기본 표현 — 테두리 상자가 아니라 **여백과 구분선**이다(D4 §3-4 · D150).
 *
 * `Card`는 독립된 항목(누를 수 있는 한 건, 떠 있어야 하는 패널)일 때만 쓴다.
 * 화면을 나누는 구획마다 카드를 씌우면 같은 무게의 상자가 나열되고, 그 안에 또 상자가 들어간다.
 */
export function Section({
  title,
  titleAs = 'h2',
  titleLevel = 'card',
  action,
  divided = false,
  className,
  children,
  ...props
}: SectionProps) {
  const titleId = useId();
  const named = title !== undefined;

  return (
    <section
      aria-labelledby={named ? titleId : undefined}
      className={cn(
        'gap-row flex min-w-0 flex-col',
        divided && 'border-line pt-panel border-t',
        className,
      )}
      {...props}
    >
      {named ? (
        <div className="gap-card flex min-h-8 items-center justify-between">
          <Title level={titleLevel} as={titleAs} id={titleId}>
            {title}
          </Title>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}
